import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/Button';
import Select from '../../../../components/Select';
import Modal from '../../../../components/Modal';
import blogStorage from '../../utils/storage';
import { renderMarkdownPreview } from '../../utils/markdown';
import './index.scss';

const ArticleEditModal = ({ 
  isOpen, 
  onClose, 
  articleId = null, 
  draftId = null,
  mode = 'create', // 'create', 'edit', 'draft'
  onSave = null 
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    categoryId: '',
    tags: []
  });
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [currentDraftId, setCurrentDraftId] = useState(draftId);
  
  const autoSaveTimerRef = useRef(null);

  // 加载数据
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      
      // 设置自动保存
      const interval = setInterval(autoSave, 30000); // 30秒自动保存
      
      return () => {
        clearInterval(interval);
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
      };
    }
  }, [isOpen]);

  // 加载文章或草稿数据
  useEffect(() => {
    if (isOpen && mode === 'edit' && articleId) {
      loadArticle(articleId);
    } else if (isOpen && mode === 'draft' && draftId) {
      loadDraft(draftId);
    } else if (isOpen && mode === 'create') {
      // 重置表单
      resetForm();
    }
  }, [isOpen, mode, articleId, draftId]);

  // 更新字数统计
  useEffect(() => {
    setWordCount(blogStorage.countWords(formData.content));
  }, [formData.content]);

  const loadInitialData = () => {
    setCategories(blogStorage.getCategories());
    setAllTags(blogStorage.getTags());
  };

  const loadArticle = (id) => {
    const articles = blogStorage.getArticles();
    const article = articles.find(art => art.id === id);
    if (article) {
      setFormData({
        title: article.title,
        content: article.content,
        categoryId: article.categoryId,
        tags: article.tags || []
      });
    }
  };

  const loadDraft = (id) => {
    const drafts = blogStorage.getDrafts();
    const draft = drafts.find(d => d.id === id);
    if (draft) {
      setFormData({
        title: draft.title,
        content: draft.content,
        categoryId: draft.categoryId,
        tags: draft.tags || []
      });
      setCurrentDraftId(id);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      categoryId: categories[0]?.id || '',
      tags: []
    });
    setCurrentDraftId(null);
    setIsPreview(false);
    setLastSaveTime(null);
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 触发自动保存延迟
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(autoSave, 5000); // 5秒后自动保存
  };

  // 处理标签输入
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const newTag = tagInput.trim();
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // 自动保存草稿
  const autoSave = () => {
    if (!formData.title.trim() && !formData.content.trim()) {
      return;
    }

    // 如果是编辑已发布的文章，不进行草稿保存
    if (mode === 'edit' && articleId) {
      return;
    }

    try {
      // 如果有当前草稿ID，更新现有草稿
      if (currentDraftId) {
        blogStorage.updateDraft(currentDraftId, formData);
      } else if (mode !== 'edit') {
        // 只在非编辑模式下创建新草稿
        const newDraft = blogStorage.addDraft(formData);
        setCurrentDraftId(newDraft.id);
      }

      setLastSaveTime(new Date());
    } catch (error) {
      console.error('自动保存失败:', error);
    }
  };

  // 手动保存草稿
  const handleSaveDraft = () => {
    if (!formData.title.trim() && !formData.content.trim()) {
      alert('请输入内容后再保存草稿');
      return;
    }

    setIsSaving(true);
    try {
      let draft;
      if (currentDraftId) {
        // 更新现有草稿
        draft = blogStorage.updateDraft(currentDraftId, formData);
        alert('草稿已更新');
      } else {
        // 创建新草稿
        draft = blogStorage.addDraft(formData);
        setCurrentDraftId(draft.id);
        alert('草稿已保存');
      }
      
      if (draft) {
        setLastSaveTime(new Date());
      }
    } catch (error) {
      console.error('保存草稿失败:', error);
      alert('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  // 发布/更新文章
  const handlePublish = () => {
    if (!formData.title.trim()) {
      alert('请输入文章标题');
      return;
    }

    if (!formData.content.trim()) {
      alert('请输入文章内容');
      return;
    }

    if (!formData.categoryId) {
      alert('请选择文章分类');
      return;
    }

    setIsSaving(true);
    try {
      let result;
      if (mode === 'edit' && articleId) {
        // 更新文章
        result = blogStorage.updateArticle(articleId, formData);
        if (result) {
          alert('文章更新成功');
        }
      } else {
        // 新建文章
        result = blogStorage.addArticle(formData);
        if (result) {
          alert('文章发布成功');
          // 如果是从草稿发布，删除对应草稿
          if (currentDraftId) {
            blogStorage.deleteDraft(currentDraftId);
            setCurrentDraftId(null);
          }
          // 跳转到内容列表页面
          onClose();
          navigate('/blog/articles');
        }
      }
      
      if (result && mode === 'edit') {
        if (onSave) onSave(result);
        onClose();
      }
    } catch (error) {
      console.error('发布文章失败:', error);
      alert('发布失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  // 预览模式
  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // 使用新的markdown预览渲染器
  const renderPreview = (content) => {
    return renderMarkdownPreview(content);
  };

  if (!isOpen) return null;

  return (
    <Modal
      visible={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? '编辑文章' : mode === 'draft' ? '编辑草稿' : '创建文章'}
    >
      <div className="article-editor-modal">
        <div className="editor-header">
          <div className="editor-stats">
            <span className="word-count">字数: {wordCount}</span>
            {lastSaveTime && (
              <span className="last-save">
                最后保存: {lastSaveTime.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="editor-actions">
            <Button 
              onClick={togglePreview}
            >
              {isPreview ? '编辑' : '预览'}
            </Button>
            {mode !== 'edit' && (
              <Button 
                onClick={handleSaveDraft}
                disabled={isSaving}
              >
                {isSaving ? '保存中...' : '保存草稿'}
              </Button>
            )}
            <Button 
              type="primary"
              onClick={handlePublish}
              disabled={isSaving}
            >
              {isSaving ? '发布中...' : mode === 'edit' ? '更新文章' : '发布文章'}
            </Button>
          </div>
        </div>

        <div className="editor-content">
          {!isPreview ? (
            <div className="editor-form">
              {/* 文章信息区块 */}
              <div className="form-section">
                <h5>文章信息</h5>
                <div className="form-group">
                  <label>文章标题</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="请输入文章标题..."
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>文章分类</label>
                  <Select
                    value={formData.categoryId}
                    onChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
                    options={[
                      { value: '', label: '请选择分类' },
                      ...categories.map(category => ({
                        value: category.id,
                        label: category.parentId ? `└ ${category.name}` : category.name
                      }))
                    ]}
                    placeholder="请选择分类"
                    dropdownPortal={true}
                  />
                </div>

                <div className="form-group">
                  <label>文章标签</label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {formData.tags.map((tag, index) => (
                        <span key={index} className="tag-item">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="tag-remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={handleTagInputChange}
                      onKeyDown={handleTagInputKeyDown}
                      placeholder="添加标签..."
                      className="tag-input"
                    />
                  </div>
                </div>
              </div>

              {/* 文章内容区块 */}
              <div className="form-section">
                <h5>文章内容</h5>
                <div className="form-group">
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="开始写作吧..."
                    className="content-editor"
                    rows={20}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="preview-content">
              <h2>{formData.title || '未命名文章'}</h2>
              <div className="article-meta">
                <span>分类: {categories.find(c => c.id === formData.categoryId)?.name || '未分类'}</span>
                {formData.tags.length > 0 && (
                  <span>标签: {formData.tags.join(', ')}</span>
                )}
                <span>字数: {wordCount}</span>
              </div>
              <div 
                className="article-content"
                dangerouslySetInnerHTML={{
                  __html: renderPreview(formData.content)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ArticleEditModal;
